/**
 * The app's side of the public Memorial Wall (WALL-contracts): turns the transport into the
 * `WallPort` the menus take. It leans on the cloud-save layer for one thing only - the save code,
 * which is what the server turns into the pseudonym an entry is filed under. No code, no publishing.
 */

import type { Storage } from '../../meta/api';
import { parseProfile, SAVE_KEY } from '../../meta/save';
import type { MemorialEntry } from '../../meta/types';
import type { WallPort, WallRead } from '../../ui/screens/wallPort';
import { browserStorage } from '../storage';
import type { CloudSave, FetchLike } from '../cloudSave/types';
import { createStandingsReporter } from './standings';
import { createStandingsTransport, type StandingsTransport } from './standingsTransport';
import { createWallTransport, type WallTransport } from './wallTransport';

export type { Standings, StandingsRecord } from './standings';
export type { PublicName, WallTransport } from './wallTransport';

/**
 * Why this device cannot publish, in the game's voice; null when it can. Pure.
 *
 * Only ever the ACTIONABLE reason. "There is no server here" is not a reason a player can do
 * anything about, so it is `available()` that answers that, and the game then says nothing at all
 * rather than advertising a feature this copy has not got.
 */
export function whyNotPublish(enabled: boolean, code: string | null): string | null {
  if (!enabled) return null;
  // Cloud saves are on by default - `pullAtBoot` mints a code the first time the server answers -
  // so there is no switch to tell him to find. Having none means the server has not answered YET.
  if (code === null) return 'THE SERVER HAS NOT ANSWERED YET. HIS NAME CAN GO UP IN A MOMENT.';
  return null;
}

export interface WallDeps {
  enabled(): boolean;
  code(): string | null;
  transport: WallTransport;
  /** The standings half of the port. Without it `standings()` is null and the panel is not drawn. */
  standings?: StandingsTransport;
  /**
   * Run before a name is sent. The server only accepts one from a save it ALREADY holds, and a
   * player's first death can come before his profile has finished going up - the push is debounced.
   * So the campaign is flushed and waited on first, and the refusal never happens.
   */
  settle?(): Promise<void>;
}

/** The port, from its parts: what the tests drive. */
export function createWallPort({ enabled, code, transport, standings, settle }: WallDeps): WallPort {
  return {
    available: () => enabled(),

    async pulse() {
      return enabled() ? await transport.pulse() : null;
    },

    async standings() {
      return enabled() && standings !== undefined ? await standings.read() : null;
    },

    async read(): Promise<WallRead> {
      if (!enabled()) return { kind: 'unreachable' };
      return await transport.read(code());
    },

    whyNotPublish: () => whyNotPublish(enabled(), code()),

    async publish(entry: MemorialEntry, photo?: Uint8Array | null): Promise<string | null> {
      const mine = code();
      const refused = whyNotPublish(enabled(), mine);
      if (refused !== null || mine === null) return refused ?? 'THERE IS NO PUBLIC MEMORIAL HERE.';
      await settle?.();
      // PHOTOS §2: the picture goes only when the player chose to show it (the offer's first button).
      const result = await transport.publish(mine, entry, photo ?? null);
      if (result.kind === 'ok') return null;
      return result.kind === 'refused' ? result.why : 'THE MEMORIAL COULD NOT BE REACHED. HE IS STILL YOURS.';
    },
  };
}

/**
 * File the standings once now, and again after every save the server ACKNOWLEDGED - and at no
 * other time. There is deliberately no second sync engine here: the cloud layer already decides
 * when this device talks to the server, and just after it took a save is the one moment the
 * server is known to hold this save, which is exactly what filing requires.
 *
 * The one at the start is for a company that has stopped changing: it still exists, and without
 * it a player who never plays again would never be counted. The reporter drops a filing whose
 * figures have not moved, so in a quiet session that first one is also the only one.
 */
function fileAfterEverySave(cloud: CloudSave, report: () => void): void {
  let seen = cloud.status().lastSyncedAt;
  cloud.subscribe(() => {
    const at = cloud.status().lastSyncedAt;
    if (at === seen) return;
    seen = at;
    report();
  });
  report();
}

/**
 * The port the app hands the menus. It rides on the same probe the cloud save made: a host with no
 * save API has no wall either, and neither costs a second request.
 */
export function startWall(cloud: CloudSave, fetchLike?: FetchLike, origin?: string, storage?: Storage): WallPort {
  const call = fetchLike ?? ((input, init) => fetch(input as RequestInfo, init as RequestInit));
  const at = origin ?? globalThis.location?.origin ?? '';
  const standings = createStandingsTransport(call, at);
  const port = createWallPort({
    enabled: () => cloud.enabled(),
    code: () => cloud.code(),
    transport: createWallTransport(call, at),
    standings,
    settle: async () => {
      cloud.flush();
      await cloud.whenIdle();
    },
  });
  // The figures come out of the profile in localStorage, which is the store of record and is
  // already up to date by the time anything here runs (`pullAtBoot` settles before the game starts).
  const reporter = createStandingsReporter({
    enabled: () => cloud.enabled(),
    code: () => cloud.code(),
    profile: () => parseProfile((storage ?? browserStorage).load(SAVE_KEY)),
    file: (code, record) => standings.file(code, record),
  });
  fileAfterEverySave(cloud, () => void reporter.report());
  return port;
}
